/**
 * Does this session hold the admin role?
 *
 * ── The comma trap, which this project has already hit ───────────────
 *
 * Better Auth's admin plugin stores roles as a COMMA-SEPARATED string, so
 * `role === 'auth-admin'` is false for a user carrying `user,auth-admin` —
 * locking out exactly the staff most likely to hold more than one role. The auth
 * service
 * has the same function for the same reason (`lib/admin-only.ts`), and it is
 * worth a file of its own here rather than an inline check, because the inline
 * version is the one that gets written wrong.
 *
 * ── This is a MESSAGE, not a control ─────────────────────────────────
 *
 * The server refuses every admin endpoint without the role, and answers 404 so
 * the surface is not advertised. Nothing here protects anything — a user who
 * edits this in devtools gets a portal full of 404s.
 *
 * What it buys is a sentence: without it, a signed-in non-admin sees every
 * screen fail with "not found" and reasonably concludes the service is broken.
 */

/**
 * The one role that is NOT staff.
 *
 * ── THIS USED TO BE THE OPPOSITE LIST, AND THAT WAS THE BUG ──────────
 *
 * It was `ADMIN_ROLES = ['admin', 'owner']`, a hand-kept copy of the auth
 * service's list, carrying a note that the two "must agree or the portal and the
 * API disagree about who is allowed in". They did agree, right up until
 * RidgeWill/auth#146 replaced two roles with five — at which point this file
 * would have told every administrator on the platform that they are not one,
 * while every request they made succeeded.
 *
 * A copied allow-list cannot survive the other end changing. An exclude-list of
 * the single role that means "not staff" can: the auth service derives its own
 * `ADMIN_ROLES` the same way, as everything except the default role, so a role
 * added there is staff in both places without anybody editing this.
 *
 * ── AND THE FAILURE DIRECTIONS ARE NOT SYMMETRIC ─────────────────────
 *
 * This is a MESSAGE, not a control — the header above says so, and the server
 * 404s every admin endpoint regardless. So admitting somebody who turns out not
 * to be staff costs them a portal full of "not found", which is recoverable and
 * obvious. Refusing somebody who IS staff costs them a screen that says the
 * platform is not theirs while the API disagrees, which is neither.
 *
 * Given that, the direction to fail in is clear, and it is the one an
 * exclude-list fails in.
 */
export const NON_ADMIN_ROLES = ['user'] as const;

export function hasAdminRole(
  rawRole: unknown,
  nonAdminRoles: readonly string[] = NON_ADMIN_ROLES,
): boolean {
  if (typeof rawRole !== 'string') return false;
  return rawRole
    .split(',')
    .map((r) => r.trim())
    .filter(Boolean)
    .some((r) => !nonAdminRoles.includes(r));
}

/**
 * The stored string, as a list.
 *
 * ── Deliberately NOT the same as the auth service's `parseRoles` ─────
 *
 * That one filters to roles it knows about, which is right on the server: an
 * unrecognised name grants nothing, so dropping it early keeps the access-
 * control statement honest.
 *
 * It is wrong here. This portal's job includes showing an administrator what is
 * actually on the account, and a role that silently disappears from the display
 * because the portal has not heard of it is the worst possible presentation —
 * the account behaves one way and the screen says another. So nothing is
 * filtered; the screens mark unknown values instead.
 *
 * Deduplicated and emptied-out, because `admin,,admin` is one role and a blank
 * entry is not a role at all.
 */
export function splitRoles(rawRole: unknown): string[] {
  if (typeof rawRole !== 'string') return [];
  return [
    ...new Set(
      rawRole
        .split(',')
        .map((r) => r.trim())
        .filter(Boolean),
    ),
  ];
}

/**
 * The roles on an account that a human may actually tick or untick.
 *
 * ── WHY THE IMPLIED ROLE HAS TO BE FILTERED OUT ──────────────────────
 *
 * `user` is implied on every account: it confers nothing, it is the default, and
 * RidgeWill/auth#146 stopped `/admin/roles` from listing it so the portal would
 * not offer a checkbox somebody could "remove".
 *
 * The role picker seeds itself from the account's stored column, which DOES
 * contain `user` — so without this the picker holds a value the server's list
 * does not, `planRoleSave` reads that as an unrecognised role, and every role
 * save on every account is refused with "the server does not list user as a
 * role". Every account, because they all have it.
 *
 * Dropping it from the picker also means a save sends only the admin roles. That
 * is correct rather than lossy: the column not naming the default role is what
 * "implied" means, and `parseRoles` on the server treats the two the same.
 */
export function assignableRoles(rawRole: unknown): string[] {
  return splitRoles(rawRole).filter((r) => !isImpliedRole(r));
}

/**
 * Is this the role every account has and nobody chose?
 *
 * One definition, because two screens now depend on it: the role picker, which
 * must not offer a checkbox for it, and the Access tab, which must not render a
 * row for it (Jon, 2026-08-28 — "user is assumed, not necessary to show this
 * one"). A row that is identical on every account and cannot be acted on is a
 * row that teaches people to skim the table it is in.
 *
 * Reads `NON_ADMIN_ROLES` rather than a literal, so the reasoning at the top of
 * this file governs both uses — including the part about why this is an
 * exclude-list of one name rather than a copied allow-list.
 */
export function isImpliedRole(slug: string): boolean {
  return NON_ADMIN_ROLES.includes(slug as never);
}

/**
 * What to show a person for a role slug.
 *
 * ── THE FALLBACK IS THE WHOLE POINT ──────────────────────────────────
 *
 * Returns the slug when there is no label for it, and that case is not
 * hypothetical in either direction: an auth service that predates
 * RidgeWill/auth#146's labels sends none, and a role held on an account that
 * this portal's server no longer defines has none either.
 *
 * Showing the raw slug there is the correct answer rather than a degraded one.
 * This portal's job includes telling an administrator what is actually on an
 * account — the same argument `splitRoles` above makes about not filtering
 * unknown roles out. A blank cell, or a label invented by title-casing the slug,
 * would both be the screen deciding it knows better than the data.
 */
export function roleLabel(
  slug: string,
  labels?: Record<string, { label: string }>,
): string {
  return labels?.[slug]?.label ?? slug;
}

/**
 * Which role is the break-glass one: NAMED by the server, or failing that,
 * DERIVED from its two lists.
 *
 * ── NAMED FIRST (RidgeWill/auth#561) ────────────────────────────────
 *
 * The derivation below rests on "`groupAssignable` leaves out exactly one
 * role", and auth#556 broke it: `dev-agent` is a second role no group may
 * carry. With two left out this returned undefined, `issuableRoles` stopped
 * treating a Global Admin as able to grant everything, and the portal could not
 * give an agent `dev-agent` at all, although the server would have. So the
 * server now names the role, and the derivation is only for an auth that
 * predates that.
 *
 * ── NOT A CONSTANT, FOR THE REASON AT THE TOP OF THIS FILE ───────────
 *
 * `global-admin` written here would be a copy of a name that lives in the auth
 * service, and this file already carries the scar from the last one: the Users
 * table tinted `admin` amber until RidgeWill/auth#270 renamed it to
 * `auth-admin`, after which the highlight matched nothing and every role
 * rendered identically. Nothing failed, and nothing said so.
 *
 * The server already sends enough to work it out. `groupAssignable` is every
 * assignable role EXCEPT the break-glass one — that exclusion is the whole
 * reason the second list exists — so the difference between them is it. A rename
 * on the far end moves this automatically, which is what the old constant could
 * not do.
 *
 * Returns undefined when the server sends no `groupAssignable` (one that
 * predates it), or when the two lists do not differ by exactly one name. Both
 * mean "do not highlight anything" rather than a guess: a highlight on the wrong
 * role is worse than none, because it tells an administrator that an ordinary
 * account is the dangerous one.
 */
export function breakGlassRole(catalogue: {
  roles: string[];
  groupAssignable?: string[];
  breakGlass?: string;
}): string | undefined {
  const { roles: allRoles, groupAssignable, breakGlass } = catalogue;
  // Named, and only if it is a role the same response lists: a name that is
  // not in `roles` is a server bug, and highlighting nothing beats guessing.
  if (breakGlass) return allRoles.includes(breakGlass) ? breakGlass : undefined;
  if (!groupAssignable) return undefined;
  const excluded = allRoles.filter((r) => !groupAssignable.includes(r));
  return excluded.length === 1 ? excluded[0] : undefined;
}
